import 'Components/atoms/vm-button/vm-button';
import 'Components/atoms/vm-icon-next/vm-icon-next';
import { html, LitElement, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { isDesktop } from 'Utils/viewport';
import Styles from './vm-slider.scss';

export type VmSliderOrientation = 'horizontal' | 'vertical';

/**
 * Scrollable strip of slotted items.
 *
 * Horizontal sliders snap to items and show prev/next controls on fine pointers.
 * Vertical sliders scrolled via mouse wheel.
 *
 * @cssprop --vm-slider-gap - Space between items. Default `16px`.
 * @cssprop --vm-slider-padding - Vertical only: padding inside the scroller. Default `0`.
 */
@customElement('vm-slider')
export class VmSlider extends LitElement {
    static override styles = Styles;

    @property({ type: String, reflect: true })
    orientation: VmSliderOrientation = 'horizontal';

    @property({ type: Number })
    activeIndex = 0;

    @state()
    private _prevDisabled = true;

    @state()
    private _nextDisabled = false;

    private _sliderContent: HTMLDivElement | null = null;

    override firstUpdated() {
        this._sliderContent = this.renderRoot.querySelector('.vm-slider__content');
        this.handleScroll();
    }

    override updated(changedProperties: PropertyValues<this>) {
        if (changedProperties.has('orientation')) {
            this.handleScroll();
        }
        if (changedProperties.has('activeIndex') && this._sliderContent) {
            this.scrollToIndex(this.activeIndex);
        }
    }

    scrollToIndex(index: number) {
        const content = this._sliderContent;
        const items = content?.querySelector('slot')?.assignedElements({ flatten: true }) || [];
        const item = items[index];

        if (!content || !item) return;

        if (this._isVertical) {
            item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
            return;
        }

        // Scroll only the container; scrollIntoView would also scroll the page.
        const contentRect = content.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        const itemLeft = content.scrollLeft + itemRect.left - contentRect.left;
        const alignmentOffset = isDesktop()
            ? (content.clientWidth - itemRect.width) / 2
            : parseFloat(getComputedStyle(content).scrollPaddingLeft) || 0;

        content.scrollTo({ left: itemLeft - alignmentOffset, behavior: 'smooth' });
    }

    private get _isVertical() {
        return this.orientation === 'vertical';
    }

    handleScroll() {
        // Controls are horizontal-only.
        if (!this._sliderContent || this._isVertical) return;

        const scrollWidth = this._sliderContent.scrollWidth;
        const outerWidth = this._sliderContent.clientWidth;
        const scrollLeft = this._sliderContent.scrollLeft;

        this._prevDisabled = scrollLeft <= 0;
        this._nextDisabled = scrollLeft >= scrollWidth - outerWidth;
    }

    getOverlap() {
        const items = this._sliderContent?.querySelector('slot')?.assignedElements({ flatten: true }) || [];
        const firstItem = items[0];
        return firstItem instanceof HTMLElement ? firstItem.offsetWidth + 16 : 0;
    }

    handleNextClick() {
        this._sliderContent?.scrollBy({
            left: this._sliderContent.clientWidth - this.getOverlap(),
            behavior: 'smooth',
        });
    }

    handlePrevClick() {
        this._sliderContent?.scrollBy({
            left: -(this._sliderContent.clientWidth - this.getOverlap()),
            behavior: 'smooth',
        });
    }

    override render() {
        const horizontal = !this._isVertical;
        const stateClasses = {
            'vm-slider__content--prev-disabled': horizontal && this._prevDisabled,
            'vm-slider__content--prev-enabled': horizontal && !this._prevDisabled,
            'vm-slider__content--next-disabled': horizontal && this._nextDisabled,
            'vm-slider__content--next-enabled': horizontal && !this._nextDisabled,
        };

        return html`
            <div class="vm-slider__content ${classMap(stateClasses)}" role="list" @scroll=${this.handleScroll}>
                <slot></slot>
            </div>
            ${this._isVertical ? nothing : this._renderControls()}
        `;
    }

    private _renderControls() {
        return html`
            <vm-button
                class="vm-slider__control vm-slider__control--prev"
                ?hidden=${this._prevDisabled}
                variant="solid"
                color-scheme="secondary"
                size="md"
                icon-only
                aria-label="Previous slide"
                @click=${this.handlePrevClick}
            >
                <vm-icon-next slot="icon-leading" name="chevron-left"></vm-icon-next>
            </vm-button>
            <vm-button
                class="vm-slider__control vm-slider__control--next"
                ?hidden=${this._nextDisabled}
                variant="solid"
                color-scheme="secondary"
                size="md"
                icon-only
                aria-label="Next slide"
                @click=${this.handleNextClick}
            >
                <vm-icon-next slot="icon-leading" name="chevron-right"></vm-icon-next>
            </vm-button>
        `;
    }
}

export default VmSlider;

declare global {
    interface HTMLElementTagNameMap {
        'vm-slider': VmSlider;
    }
}
